'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { formatTimestampDate } from '@/lib/date-utils';
import {
  CalendarDays,
  ChevronRight,
  Link2 as LinkIcon,
  MapPin,
  Play,
  Ticket,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type {
  PostAudition,
  PostEvent,
  PostLinkPreview,
  PostMedia,
} from '@kit/community';
import { auditionPath, competitionPath, eventPath } from '@kit/shared/handles';
import { cdnImageUrl } from '@kit/shared/storage';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { cn } from '@kit/ui/cn';
import { hashSeededGradient } from '@kit/ui/entity-card';
import { ImageLightbox, type ViewerImage } from '@kit/ui/image-viewer';
import { Trans } from '@kit/ui/trans';

/**
 * Shared attachment renderers for post surfaces (feed card + post detail).
 * One edit surface: any change to how a post's audition, media, or links
 * render happens here for both pages.
 *
 * Design references: LinkedIn job update (cover → title → org → CTA),
 * Facebook Events (urgency line over cover), Lex event chip (compact embed
 * inside long text posts).
 */

const URGENCY_WINDOW_DAYS = 14;
const MS_PER_DAY = 86_400_000;

/**
 * Whole days until the closing instant. `closing_date` is timestamptz, so
 * plain Date arithmetic on the instant is correct (no calendar-date
 * timezone shift). Returns null when there is no closing date.
 */
function closesInDays(closingDate: string | null): number | null {
  if (!closingDate) return null;
  const closes = new Date(closingDate).getTime();
  if (Number.isNaN(closes)) return null;
  return Math.ceil((closes - Date.now()) / MS_PER_DAY);
}

function auditionHref(audition: PostAudition): string {
  if (!audition.slug) return '#';
  return audition.is_competition
    ? competitionPath(audition.slug)
    : auditionPath(audition.slug);
}

/**
 * Deadline chip/line shared by both embed variants. `overlay` renders
 * glassy pills legible on top of the cover photo; `inline` renders plain
 * text for the compact chip card.
 */
function AuditionDeadline({
  audition,
  variant = 'inline',
  className,
}: {
  audition: PostAudition;
  variant?: 'overlay' | 'inline';
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const days = closesInDays(audition.closingDate);
  const isClosed =
    audition.status !== 'published' || (days !== null && days < 0);
  const overlay = variant === 'overlay';

  if (isClosed) {
    return (
      <Badge
        variant="secondary"
        className={cn(
          overlay &&
            'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
          className,
        )}
      >
        <Trans i18nKey="community:feed.auditionClosed" defaults="Closed" />
      </Badge>
    );
  }

  if (days === null) return null;

  if (days <= URGENCY_WINDOW_DAYS) {
    const label =
      days === 0
        ? t('community:feed.closesToday', 'Closes today')
        : // t() so i18next plural rules apply (closesInDays_one/_other)
          t('community:feed.closesInDays', {
            count: days,
            defaultValue: 'Closes in {{count}} days',
          });
    return overlay ? (
      <Badge className={className}>{label}</Badge>
    ) : (
      <span className={cn('text-primary text-sm font-medium', className)}>
        {label}
      </span>
    );
  }

  const dateLabel = (
    <Trans
      i18nKey="community:feed.closesOn"
      defaults="Closes {{date}}"
      values={{
        date: formatTimestampDate(
          audition.closingDate,
          { month: 'short', day: 'numeric', year: 'numeric' },
          i18n.language,
        ),
      }}
    />
  );

  return overlay ? (
    <Badge
      variant="secondary"
      className={cn(
        'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
        className,
      )}
    >
      {dateLabel}
    </Badge>
  ) : (
    <span className={cn('text-muted-foreground text-sm', className)}>
      {dateLabel}
    </span>
  );
}

function AuditionTypeBadge({ audition }: { audition: PostAudition }) {
  return (
    <Badge
      variant="secondary"
      className="bg-background/80 text-foreground backdrop-blur-sm"
    >
      <Ticket className="mr-1 h-3 w-3" aria-hidden="true" />
      {audition.is_competition ? (
        <Trans
          i18nKey="community:feed.linkedCompetition"
          defaults="Competition"
        />
      ) : (
        <Trans i18nKey="community:feed.linkedAudition" defaults="Audition" />
      )}
    </Badge>
  );
}

function AuditionTitle({ audition }: { audition: PostAudition }) {
  return (
    <>
      {audition.title ?? (
        <Trans
          i18nKey="community:feed.untitledAudition"
          defaults="Untitled audition"
        />
      )}
    </>
  );
}

/**
 * Floating identity pill rendered INSIDE a hero cover (top-left): small
 * avatar + name on a glassy dark chip. Kept as an absolutely-positioned
 * SIBLING of the cover link (never nested inside it — nested anchors are
 * invalid HTML).
 */
export function HeroIdentityChip({
  name,
  avatarUrl,
  href,
}: {
  name: string;
  avatarUrl: string | null;
  href?: string;
}) {
  const content = (
    <span className="flex min-w-0 items-center gap-1.5 rounded-md bg-black/55 py-1 pr-3 pl-1 backdrop-blur-sm">
      <Avatar className="h-6 w-6">
        <AvatarImage src={avatarUrl ?? undefined} />
        <AvatarFallback className="text-[10px]">
          {name[0]?.toUpperCase() ?? '?'}
        </AvatarFallback>
      </Avatar>
      <span className="truncate text-sm font-medium text-white">{name}</span>
    </span>
  );

  return (
    <span className="absolute top-3 left-3 z-10 max-w-[70%]">
      {href ? (
        <Link href={href} className="block hover:opacity-90">
          {content}
        </Link>
      ) : (
        content
      )}
    </span>
  );
}

/**
 * Hero embed — the post IS the audition, so the cover IS the card: one
 * full-bleed 16:9 visual with everything overlaid (no footer chrome).
 * Top-left: floating identity chip (passed by the post surface). Top-right:
 * deadline pill. Bottom gradient: type badge + title. The whole cover links
 * to the audition. Coverless auditions get a hash-seeded gradient.
 */
function AuditionHeroCard({
  audition,
  identity,
}: {
  audition: PostAudition;
  identity?: React.ReactNode;
}) {
  const href = auditionHref(audition);
  const initials = (audition.title ?? 'A').slice(0, 2).toUpperCase();

  return (
    // No own rounding: rendered as the card's full-bleed hero, the parent
    // Card clips the top corners and the bottom must sit FLUSH on the
    // card's action layer (rounded bottom read as a visual bug).
    <div className="relative overflow-hidden">
      {identity}
      <AuditionDeadline
        audition={audition}
        variant="overlay"
        className="absolute top-3 right-3 z-10"
      />
      <Link href={href} className="group block">
        <div
          className="relative aspect-[16/9] w-full overflow-hidden"
          style={
            audition.coverUrl
              ? undefined
              : { background: hashSeededGradient(audition.id) }
          }
        >
          {audition.coverUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={cdnImageUrl(audition.coverUrl, 'GALLERY') ?? undefined}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          ) : (
            <span
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center text-4xl font-semibold text-white/25"
            >
              {initials}
            </span>
          )}
          {/* Bottom gradient carrying badge + title */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent p-4 pt-12">
            <AuditionTypeBadge audition={audition} />
            <h3 className="mt-1.5 line-clamp-2 text-lg leading-snug font-semibold text-white">
              <AuditionTitle audition={audition} />
            </h3>
          </div>
        </div>
      </Link>
    </div>
  );
}

/**
 * Chip embed — the audition referenced from a longer text post. Compact
 * horizontal card: cover thumbnail (or gradient), kicker, title, deadline.
 */
function AuditionChipCard({ audition }: { audition: PostAudition }) {
  const href = auditionHref(audition);
  const initials = (audition.title ?? 'A').slice(0, 1).toUpperCase();

  return (
    <Link
      href={href}
      className="border-border hover:bg-muted/50 group flex items-center gap-3 overflow-hidden rounded-lg border p-3 transition-colors"
    >
      <div
        className="relative h-[72px] w-[96px] shrink-0 overflow-hidden rounded-lg"
        style={
          audition.coverUrl
            ? undefined
            : { background: hashSeededGradient(audition.id) }
        }
      >
        {audition.coverUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cdnImageUrl(audition.coverUrl, 'THUMBNAIL') ?? undefined}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center text-xl font-semibold text-white/30"
          >
            {initials}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs tracking-wide uppercase">
          {audition.is_competition ? (
            <Trans
              i18nKey="community:feed.linkedCompetition"
              defaults="Competition"
            />
          ) : (
            <Trans
              i18nKey="community:feed.linkedAudition"
              defaults="Audition"
            />
          )}
        </span>
        <span className="text-foreground line-clamp-1 font-medium group-hover:underline">
          <AuditionTitle audition={audition} />
        </span>
        <AuditionDeadline audition={audition} />
      </div>

      <ChevronRight
        className="text-muted-foreground h-4 w-4 shrink-0"
        aria-hidden="true"
      />
    </Link>
  );
}

export function AuditionEmbed({
  audition,
  variant,
  identity,
}: {
  audition: PostAudition;
  variant: 'hero' | 'chip';
  // Floating top-left identity chip (post author) — hero only.
  identity?: React.ReactNode;
}) {
  return variant === 'hero' ? (
    <AuditionHeroCard audition={audition} identity={identity} />
  ) : (
    <AuditionChipCard audition={audition} />
  );
}

/**
 * Threshold under which an audition post renders the hero embed: a short
 * body reads as a lead-in line for the entity; a longer one is prose that
 * merely references it. Counted on the body with bare URLs stripped so a
 * pasted link doesn't flip the variant.
 */
const HERO_BODY_MAX_CHARS = 180;
const URL_STRIP_PATTERN = /https?:\/\/\S+/g;

export function auditionEmbedVariant(body: string): 'hero' | 'chip' {
  const stripped = body.replace(URL_STRIP_PATTERN, '').trim();
  return stripped.length <= HERO_BODY_MAX_CHARS ? 'hero' : 'chip';
}

// ---------------------------------------------------------------------------
// Community event embed
// ---------------------------------------------------------------------------

function eventHref(event: PostEvent): string {
  if (!event.slug) return '#';
  return eventPath(event.slug);
}

/** A community event is "live" (linkable, open deadline) when published-ish. */
function isEventClosed(event: PostEvent, days: number | null): boolean {
  return (
    event.status === 'closed' ||
    event.status === 'cancelled' ||
    (days !== null && days < 0)
  );
}

/**
 * Deadline chip/line for a community event — mirrors `AuditionDeadline`
 * but reads `applicationsToDate` and the community-event status vocabulary.
 * `overlay` renders glassy pills legible on the cover; `inline` renders
 * plain text for the compact chip card.
 */
function EventDeadline({
  event,
  variant = 'inline',
  className,
}: {
  event: PostEvent;
  variant?: 'overlay' | 'inline';
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const days = closesInDays(event.applicationsToDate);
  const overlay = variant === 'overlay';

  if (isEventClosed(event, days)) {
    return (
      <Badge
        variant="secondary"
        className={cn(
          overlay &&
            'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
          className,
        )}
      >
        <Trans i18nKey="community:feed.auditionClosed" defaults="Closed" />
      </Badge>
    );
  }

  if (days === null) return null;

  if (days <= URGENCY_WINDOW_DAYS) {
    const label =
      days === 0
        ? t('community:feed.closesToday', 'Closes today')
        : t('community:feed.closesInDays', {
            count: days,
            defaultValue: 'Closes in {{count}} days',
          });
    return overlay ? (
      <Badge className={className}>{label}</Badge>
    ) : (
      <span className={cn('text-primary text-sm font-medium', className)}>
        {label}
      </span>
    );
  }

  const dateLabel = (
    <Trans
      i18nKey="community:feed.closesOn"
      defaults="Closes {{date}}"
      values={{
        date: formatTimestampDate(
          event.applicationsToDate,
          { month: 'short', day: 'numeric', year: 'numeric' },
          i18n.language,
        ),
      }}
    />
  );

  return overlay ? (
    <Badge
      variant="secondary"
      className={cn(
        'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
        className,
      )}
    >
      {dateLabel}
    </Badge>
  ) : (
    <span className={cn('text-muted-foreground text-sm', className)}>
      {dateLabel}
    </span>
  );
}

/**
 * Human label for a community event's `event_type`. Known enum values map
 * to a translated label; anything unrecognized (or null) falls back to the
 * generic "Event" badge copy.
 */
function EventTypeLabel({ eventType }: { eventType: string | null }) {
  const { t } = useTranslation();
  const fallback = t('community:feed.eventBadge', 'Event');
  if (!eventType) return <>{fallback}</>;
  return (
    <>
      {t(`community:feed.eventTypes.${eventType}`, { defaultValue: fallback })}
    </>
  );
}

function EventTypeBadge({ event }: { event: PostEvent }) {
  return (
    <Badge
      variant="secondary"
      className="bg-background/80 text-foreground backdrop-blur-sm"
    >
      <CalendarDays className="mr-1 h-3 w-3" aria-hidden="true" />
      <EventTypeLabel eventType={event.eventType} />
    </Badge>
  );
}

function EventTitle({ event }: { event: PostEvent }) {
  return (
    <>
      {event.title ?? (
        <Trans
          i18nKey="community:feed.untitledEvent"
          defaults="Untitled event"
        />
      )}
    </>
  );
}

/** Concise date span: "Jul 15 – Aug 2, 2024" (or a single date). */
function formatEventDates(
  start: string | null,
  end: string | null,
): string | null {
  if (!start) return null;
  const from = formatTimestampDate(start);
  if (!end || end === start) return from;
  const to = formatTimestampDate(end);
  return from && to ? `${from} – ${to}` : (from ?? to);
}

/**
 * The two facts that matter most for a dance event: WHEN (its date span, from
 * community_event_dates — distinct from the application deadline) and WHERE
 * (city, country). Rendered over the cover's bottom gradient, white on the
 * dark scrim so it stays legible on any photo. Each fact is omitted when
 * absent, so the row never shows an empty icon.
 */
function EventMeta({
  event,
  className,
}: {
  event: PostEvent;
  className?: string;
}) {
  const dates = formatEventDates(event.startDate, event.endDate);
  const location = [event.city, event.country].filter(Boolean).join(', ');
  if (!dates && !location) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-white/90',
        className,
      )}
    >
      {dates ? (
        <span className="inline-flex items-center gap-1">
          <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
          {dates}
        </span>
      ) : null}
      {location ? (
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
          {location}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Hero embed — the post IS the event, so the cover IS the card: one
 * full-bleed 16:9 visual with everything overlaid. Top-left: floating
 * identity chip. Top-right: deadline pill. Bottom gradient: type badge +
 * title. The whole cover links to `/events/<slug>` (only when the event has
 * a slug); coverless events get a hash-seeded gradient.
 */
function EventHeroCard({
  event,
  identity,
}: {
  event: PostEvent;
  identity?: React.ReactNode;
}) {
  const href = eventHref(event);
  const initials = (event.title ?? 'E').slice(0, 2).toUpperCase();
  const linkable = href !== '#';

  const cover = (
    <div
      className="relative aspect-[16/9] w-full overflow-hidden"
      style={
        event.coverUrl
          ? undefined
          : { background: hashSeededGradient(event.id) }
      }
    >
      {event.coverUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={cdnImageUrl(event.coverUrl, 'GALLERY') ?? undefined}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
      ) : (
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center text-4xl font-semibold text-white/25"
        >
          {initials}
        </span>
      )}
      {/* Bottom gradient carrying badge + title + the key facts (when / where) */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent p-4 pt-12">
        <EventTypeBadge event={event} />
        <h3 className="mt-1.5 line-clamp-2 text-lg leading-snug font-semibold text-white">
          <EventTitle event={event} />
        </h3>
        <EventMeta event={event} className="mt-1.5" />
      </div>
    </div>
  );

  return (
    // No own rounding — same full-bleed hero rules as AuditionHeroCard.
    <div className="relative overflow-hidden">
      {identity}
      <EventDeadline
        event={event}
        variant="overlay"
        className="absolute top-3 right-3 z-10"
      />
      {linkable ? (
        <Link href={href} className="group block">
          {cover}
        </Link>
      ) : (
        <div className="group block">{cover}</div>
      )}
    </div>
  );
}

/**
 * Chip embed — the community event referenced from a longer text post.
 * Compact horizontal card: cover thumbnail (or gradient), kicker, title,
 * deadline. Links to `/events/<slug>` when the event has a slug.
 */
function EventChipCard({ event }: { event: PostEvent }) {
  const href = eventHref(event);
  const initials = (event.title ?? 'E').slice(0, 1).toUpperCase();
  const linkable = href !== '#';

  const inner = (
    <>
      <div
        className="relative h-[72px] w-[96px] shrink-0 overflow-hidden rounded-lg"
        style={
          event.coverUrl
            ? undefined
            : { background: hashSeededGradient(event.id) }
        }
      >
        {event.coverUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cdnImageUrl(event.coverUrl, 'THUMBNAIL') ?? undefined}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center text-xl font-semibold text-white/30"
          >
            {initials}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs tracking-wide uppercase">
          <EventTypeLabel eventType={event.eventType} />
        </span>
        <span className="text-foreground line-clamp-1 font-medium group-hover:underline">
          <EventTitle event={event} />
        </span>
        <EventDeadline event={event} />
      </div>

      <ChevronRight
        className="text-muted-foreground h-4 w-4 shrink-0"
        aria-hidden="true"
      />
    </>
  );

  const cardClass =
    'border-border group flex items-center gap-3 overflow-hidden rounded-lg border p-3';

  return linkable ? (
    <Link
      href={href}
      className={cn(cardClass, 'hover:bg-muted/50 transition-colors')}
    >
      {inner}
    </Link>
  ) : (
    <div className={cardClass}>{inner}</div>
  );
}

export function EventEmbed({
  event,
  variant,
  identity,
}: {
  event: PostEvent;
  variant: 'hero' | 'chip';
  // Floating top-left identity chip (post author) — hero only.
  identity?: React.ReactNode;
}) {
  return variant === 'hero' ? (
    <EventHeroCard event={event} identity={identity} />
  ) : (
    <EventChipCard event={event} />
  );
}

/**
 * Same short-body-is-a-lead-in heuristic as `auditionEmbedVariant`: a short
 * body renders the event as a hero, a longer one as a compact chip inside
 * prose.
 */
export function eventEmbedVariant(body: string): 'hero' | 'chip' {
  return auditionEmbedVariant(body);
}

// ---------------------------------------------------------------------------
// Media grid
// ---------------------------------------------------------------------------

function isVideo(item: PostMedia): boolean {
  return item.mediaType === 'video';
}

/** Display source for a grid tile: videos show their poster. */
function tileSrc(item: PostMedia): string | null {
  return isVideo(item) ? (item.thumbnailUrl ?? item.url) : item.url;
}

function MediaTile({
  item,
  className,
  overlayCount,
  onOpen,
}: {
  item: PostMedia;
  className?: string;
  overlayCount?: number;
  onOpen: () => void;
}) {
  const src = tileSrc(item);
  if (!src) return null;

  return (
    // eslint-disable-next-line kit-components/use-kit-primitives -- media tile: structural wrapper around a full-bleed image sized by the parent's aspect-ratio; @kit/ui Button's fixed h-9 + padding + inline-flex chrome would break the media-grid tile
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'group relative block w-full overflow-hidden focus-visible:outline-2',
        className,
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
      />
      {isVideo(item) && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-black/60 backdrop-blur-sm">
            <Play
              className="ml-0.5 h-5 w-5 fill-white text-white"
              aria-hidden="true"
            />
          </span>
        </span>
      )}
      {overlayCount !== undefined && overlayCount > 0 && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-xl font-semibold text-white">
          +{overlayCount}
        </span>
      )}
    </button>
  );
}

/**
 * Rounded media grid (Threads/X-style): 1 = natural up to 4:5; 2 = two
 * columns; 3 = one tall + two stacked; 4+ = 2×2 with a "+N" overlay.
 * Images open in the shared lightbox; video tiles show a poster + play
 * glyph and open in the lightbox as well (poster; playback happens on the
 * media's own page — no inline autoplay by design).
 */
export function PostMediaGrid({
  media,
  identity,
}: {
  media: PostMedia[];
  // Floating top-left identity chip (post author) for full-bleed usage.
  identity?: React.ReactNode;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const resolved = useMemo(
    () => media.filter((item) => Boolean(tileSrc(item))),
    [media],
  );

  const viewerImages = useMemo<ViewerImage[]>(
    () =>
      resolved.map((item) => ({
        id: item.id,
        src: tileSrc(item)!,
        thumbnailSrc: item.thumbnailUrl ?? undefined,
      })),
    [resolved],
  );

  if (resolved.length === 0) return null;

  const openAt = (index: number) => () => setLightboxIndex(index);
  const count = resolved.length;

  let grid: React.ReactNode;
  if (count === 1) {
    const item = resolved[0]!;
    // Reserve space from known dimensions; cap tall portraits at 4:5.
    const ratio =
      item.width && item.height
        ? Math.max(item.width / item.height, 4 / 5)
        : 16 / 9;
    grid = (
      <div style={{ aspectRatio: `${ratio}` }}>
        <MediaTile item={item} className="h-full" onOpen={openAt(0)} />
      </div>
    );
  } else if (count === 2) {
    grid = (
      <div className="grid aspect-[16/9] grid-cols-2 gap-0.5">
        {resolved.map((item, i) => (
          <MediaTile key={item.id} item={item} onOpen={openAt(i)} />
        ))}
      </div>
    );
  } else if (count === 3) {
    grid = (
      <div className="grid aspect-[16/9] grid-cols-2 gap-0.5">
        <MediaTile item={resolved[0]!} onOpen={openAt(0)} />
        <div className="grid grid-rows-2 gap-0.5">
          <MediaTile item={resolved[1]!} onOpen={openAt(1)} />
          <MediaTile item={resolved[2]!} onOpen={openAt(2)} />
        </div>
      </div>
    );
  } else {
    grid = (
      <div className="grid aspect-square grid-cols-2 grid-rows-2 gap-0.5">
        {resolved.slice(0, 4).map((item, i) => (
          <MediaTile
            key={item.id}
            item={item}
            onOpen={openAt(i)}
            overlayCount={i === 3 ? count - 4 : undefined}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <div
        className={
          identity
            ? 'relative overflow-hidden'
            : 'relative overflow-hidden rounded-lg'
        }
      >
        {identity}
        {grid}
      </div>
      <ImageLightbox
        images={viewerImages}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        open={lightboxIndex !== null}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Link attachment
// ---------------------------------------------------------------------------

// One in-flight/settled unfurl per URL per page load — cold links call
// /api/link-preview once (which caches server-side for every future
// viewer); duplicates of the same URL across cards share the promise.
const previewWarmups = new Map<string, Promise<PostLinkPreview | null>>();

function warmLinkPreview(href: string): Promise<PostLinkPreview | null> {
  const existing = previewWarmups.get(href);
  if (existing) return existing;
  const promise = fetch(`/api/link-preview?url=${encodeURIComponent(href)}`)
    .then((res) => (res.ok ? res.json() : { preview: null }))
    .then((body: { preview?: PostLinkPreview | null }) => body.preview ?? null)
    .catch(() => null);
  previewWarmups.set(href, promise);
  return promise;
}

/**
 * Link attachment. Renders the rich unfurl card (Threads/X-style: OG image
 * on top, domain kicker, title, clamped description) when preview metadata
 * exists; falls back to the compact hostname card otherwise. Cold links
 * (no server-attached preview) warm the shared cache via /api/link-preview
 * and upgrade in place when metadata arrives.
 */
export function LinkAttachment({
  href,
  preview: initialPreview,
}: {
  href: string;
  preview?: PostLinkPreview | null;
}) {
  const [preview, setPreview] = useState<PostLinkPreview | null>(
    initialPreview ?? null,
  );
  // External og:image loaded raw (no proxy); hotlink-blocked / expired / 404
  // origins would otherwise render a broken-image box. On load failure we
  // record the failed URL and drop the image (falling back to the text-only
  // rich card, or the compact card). Keying on the URL rather than a boolean
  // means a later preview with a different image self-heals with no effect.
  const [erroredImageUrl, setErroredImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (initialPreview !== undefined && initialPreview !== null) return;
    let cancelled = false;
    void warmLinkPreview(href).then((fetched) => {
      if (!cancelled && fetched) setPreview(fetched);
    });
    return () => {
      cancelled = true;
    };
  }, [href, initialPreview]);

  let hostname = href;
  let path = '';

  try {
    const url = new URL(href);
    hostname = url.hostname.replace(/^www\./, '');
    path = `${url.pathname}${url.search}`.replace(/^\/$/, '');
  } catch {
    // keep raw href as hostname
  }

  const showImage =
    Boolean(preview?.imageUrl) && preview?.imageUrl !== erroredImageUrl;

  if (preview && (preview.title || showImage)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="border-border hover:bg-muted/50 group block overflow-hidden rounded-lg border transition-colors"
      >
        {showImage && (
          <div className="bg-muted aspect-[1.91/1] w-full overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={cdnImageUrl(preview.imageUrl, 'GALLERY') ?? undefined}
              alt=""
              loading="lazy"
              onError={() => setErroredImageUrl(preview.imageUrl)}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </div>
        )}
        <div className="flex flex-col gap-0.5 p-3">
          <span className="text-muted-foreground truncate text-xs">
            {preview.siteName ?? hostname}
          </span>
          {preview.title && (
            <span className="text-foreground line-clamp-2 text-sm font-medium group-hover:underline">
              {preview.title}
            </span>
          )}
          {preview.description && (
            <span className="text-muted-foreground line-clamp-2 text-xs">
              {preview.description}
            </span>
          )}
        </div>
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="border-border hover:bg-muted/50 group flex items-center gap-3 rounded-lg border p-3 transition-colors"
    >
      <div className="bg-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
        <LinkIcon
          className="text-muted-foreground h-4 w-4"
          aria-hidden="true"
        />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="text-foreground truncate text-sm font-medium group-hover:underline">
          {hostname}
        </span>
        {path && (
          <span className="text-muted-foreground truncate text-xs">{path}</span>
        )}
      </div>
    </a>
  );
}
